import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Download } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import DataTable from "../../components/dashboard/DataTable";
import Button from "../../components/ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import { fetchSmsLogs } from "../../services/institutionWorkspaceApi";
import { exportReport } from "../../utils/exportReport";
import { showError, showSuccess } from "../../utils/toast";
import { personNameColumns } from "../../utils/personName";

const TYPES = [
  ["", "All types"],
  ["reminder", "Reminders"],
  ["upload", "Uploads"],
  ["payment", "Payments"],
  ["other", "Others"],
];

const STATUSES = [
  ["", "Any status"],
  ["sent", "Sent"],
  ["failed", "Failed"],
];

const EMPTY = {
  type: "",
  status: "",
  search: "",
  date_from: "",
  date_to: "",
};

const TYPE_CLASS = {
  reminder: "bg-sky-100 text-sky-800",
  upload: "bg-amber-100 text-amber-900",
  payment: "bg-emerald-100 text-emerald-800",
  other: "bg-primary/10 text-primary",
};

const EXPORT_COLUMNS = [
  ["Sent", "sent_at"],
  ["Type", "sms_type"],
  ["Customer", "customer_name"],
  ["Phone", "recipient_phone"],
  ["Case", "case_code"],
  ["Account", "account_number"],
  ["Occasion", "occasion"],
  ["Due on", "due_on"],
  ["Status", "status"],
  ["Message", "message"],
];

function TypeBadge({ value }) {
  const key = String(value || "other").toLowerCase();
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold capitalize ${TYPE_CLASS[key] || TYPE_CLASS.other}`}>
      {key}
    </span>
  );
}

function fieldClass() {
  return "mt-1 w-full rounded-lg border border-primary/15 bg-white px-3 py-2 text-sm text-primary";
}

export default function SmsLogsPage() {
  const token = useSelector((state) => state.auth.token);
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [format, setFormat] = useState("Excel");

  useEffect(() => {
    let active = true;
    if (USE_MOCK_AUTH || !token) {
      setRows([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchSmsLogs(token, applied)
      .then((messages) => {
        if (active) setRows(Array.isArray(messages) ? messages : []);
      })
      .catch((error) => {
        if (active) showError(error.message || "Could not load SMS logs.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, applied]);

  const setField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));

  const applyFilters = (event) => {
    event.preventDefault();
    setApplied({ ...draft, search: draft.search.trim() });
  };

  const resetFilters = () => {
    setDraft(EMPTY);
    setApplied(EMPTY);
  };

  const handleExport = async () => {
    if (!token || USE_MOCK_AUTH) return;
    setExporting(true);
    try {
      const messages = await fetchSmsLogs(token, { ...applied, limit: 5000 });
      const sheetRows = (messages.length ? messages : [{}]).map((row) =>
        Object.fromEntries(EXPORT_COLUMNS.map(([label, key]) => [label, row[key] ?? ""]))
      );
      const period =
        applied.date_from || applied.date_to
          ? `${applied.date_from || "…"} to ${applied.date_to || "…"}`
          : "All dates";
      exportReport(
        format,
        {
          title: "SMS log",
          type: "SMS",
          period,
          report: { Rows: sheetRows },
        },
        "sms-message-log"
      );
      showSuccess(`Exported ${messages.length} message${messages.length === 1 ? "" : "s"} as ${format}.`);
    } catch (error) {
      showError(error.message || "Could not export the SMS log.");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    { key: "sent_at", header: "Sent" },
    {
      key: "sms_type",
      header: "Type",
      render: (row) => <TypeBadge value={row.sms_type} />,
    },
    ...personNameColumns(),
    { key: "case_code", header: "Case" },
    { key: "recipient_phone", header: "Phone" },
    { key: "account_number", header: "Account" },
    { key: "occasion", header: "Occasion" },
    { key: "due_on", header: "Due on" },
    {
      key: "status",
      header: "Status",
      render: (row) => <span className="capitalize">{row.status === "queued" ? "sent" : row.status || "sent"}</span>,
    },
    { key: "message", header: "Message" },
  ];

  return (
    <PageShell
      title="SMS log"
      description="Filter reminders, uploads, payments, and other texts, then export the same set."
      actions={
        <div className="flex items-center gap-2">
          <select
            value={format}
            onChange={(event) => setFormat(event.target.value)}
            className="rounded-lg border border-primary/10 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary"
          >
            {["Excel", "CSV", "Text", "PDF"].map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={handleExport} loading={exporting} disabled={loading && !rows.length}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        </div>
      }
    >
      <form onSubmit={applyFilters} className="mb-4 rounded-xl border border-primary/[0.07] bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="text-xs font-semibold text-primary/70">
            From
            <input type="date" value={draft.date_from} onChange={(event) => setField("date_from", event.target.value)} className={fieldClass()} />
          </label>
          <label className="text-xs font-semibold text-primary/70">
            To
            <input type="date" value={draft.date_to} onChange={(event) => setField("date_to", event.target.value)} className={fieldClass()} />
          </label>
          <label className="text-xs font-semibold text-primary/70">
            Type
            <select value={draft.type} onChange={(event) => setField("type", event.target.value)} className={fieldClass()}>
              {TYPES.map(([value, label]) => (
                <option key={label} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold text-primary/70">
            Status
            <select value={draft.status} onChange={(event) => setField("status", event.target.value)} className={fieldClass()}>
              {STATUSES.map(([value, label]) => (
                <option key={label} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs font-semibold text-primary/70 sm:col-span-2">
            Customer, phone, account, or case
            <input value={draft.search} onChange={(event) => setField("search", event.target.value)} className={fieldClass()} />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="submit" variant="accent" size="sm">
            Apply filters
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
            Reset
          </Button>
        </div>
      </form>
      <DataTable
        data={rows}
        columns={columns}
        pageSize={10}
        emptyMessage={loading ? "Loading SMS logs…" : "No SMS logs match these filters."}
      />
    </PageShell>
  );
}
